/**
 * `W5-T09` §4.9 — the caller's receipts and statements: the provisional banner, then one row per
 * document (kind, number, date, total) linking to it, newest first. AC 35.
 */
import { type ReactElement } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { type MoneyDocumentSummary } from '@marketplace/contracts';
import { formatMoney } from '../../checkout/money.js';
import { documentPath } from './documents.js';
import { documentDate, type DocumentLocale } from './rows.js';

export function DocumentList({
  items,
  locale,
  nextHref,
}: {
  items: readonly MoneyDocumentSummary[];
  locale: DocumentLocale;
  /** The next page, or `null` on the last. */
  nextHref: string | null;
}): ReactElement {
  const { t } = useTranslation();
  return (
    <article data-testid="documents">
      <h1>{t('documents.list.title')}</h1>
      <p className="mp-lead" role="note" data-testid="documents-banner">
        <strong>{t('documents.provisional.banner')}</strong>
      </p>
      {items.length === 0 ? (
        <p data-testid="documents-empty">{t('documents.list.empty')}</p>
      ) : (
        <ul data-testid="documents-list">
          {items.map((item) => (
            <li key={item.id}>
              <Link to={documentPath(locale, item.id)}>
                {t(`documents.kind.${item.kind}`)} {item.number}
              </Link>{' '}
              · {documentDate(item.issuedAt, locale)} · {formatMoney(item.total, locale)}
            </li>
          ))}
        </ul>
      )}
      {nextHref === null ? null : (
        <p>
          <Link to={nextHref}>{t('results.more')}</Link>
        </p>
      )}
    </article>
  );
}
